
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { EpisodeCategory, EpisodeInsert } from "../types";
import { EpisodeFormErrors } from "../validation/EpisodeFormValidation";
import { ThumbnailUpload } from "./ThumbnailUpload";

interface EpisodeFormFieldsProps {
  formData: EpisodeInsert;
  categories: EpisodeCategory[];
  errors: EpisodeFormErrors;
  isLoading: boolean;
  onFieldChange: (field: keyof EpisodeInsert, value: any) => void;
  onErrorClear: (field: string) => void;
}

export const EpisodeFormFields = ({
  formData,
  categories,
  errors,
  isLoading,
  onFieldChange,
  onErrorClear
}: EpisodeFormFieldsProps) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => {
              onFieldChange('title', e.target.value);
              if (errors.title) onErrorClear('title');
            }}
            placeholder="Episode title"
            required
            disabled={isLoading}
            className={errors.title ? "border-red-500" : ""}
          />
          {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="category_id">Category *</Label>
          <Select 
            value={formData.category_id} 
            onValueChange={(value) => {
              onFieldChange('category_id', value);
              if (errors.category_id) onErrorClear('category_id');
            }}
            disabled={isLoading}
          >
            <SelectTrigger className={errors.category_id ? "border-red-500" : ""}>
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.category_id && <p className="text-red-500 text-sm">{errors.category_id}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="duration">Duration</Label>
          <Input
            id="duration"
            value={formData.duration}
            onChange={(e) => onFieldChange('duration', e.target.value)}
            placeholder="e.g., 10:30"
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select 
            value={formData.status} 
            onValueChange={(value: "draft" | "published" | "archived") => onFieldChange('status', value)}
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="published">Published</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="video_platform">Video Platform</Label>
          <Select 
            value={formData.video_platform || ""} 
            onValueChange={(value: "youtube" | "vimeo") => onFieldChange('video_platform', value)}
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose video platform" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="youtube">YouTube</SelectItem>
              <SelectItem value="vimeo">Vimeo</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="youtube_id">YouTube ID</Label>
          <Input
            id="youtube_id"
            value={formData.youtube_id}
            onChange={(e) => onFieldChange('youtube_id', e.target.value)}
            placeholder="YouTube video ID"
            disabled={isLoading || formData.video_platform === "vimeo"}
          />
          {formData.video_platform === "vimeo" && (
            <p className="text-sm text-gray-500">Disabled - Vimeo platform selected</p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="vimeo_id">Vimeo ID</Label>
          <Input
            id="vimeo_id"
            value={formData.vimeo_id}
            onChange={(e) => onFieldChange('vimeo_id', e.target.value)}
            placeholder="Vimeo video ID"
            disabled={isLoading || formData.video_platform === "youtube"}
          />
          {formData.video_platform === "youtube" && (
            <p className="text-sm text-gray-500">Disabled - YouTube platform selected</p>
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <ThumbnailUpload
            currentPath={formData.thumbnail_url}
            onPathChange={(path) => onFieldChange('thumbnail_url', path)}
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => onFieldChange('description', e.target.value)}
          placeholder="Episode description"
          rows={3}
          disabled={isLoading}
        />
      </div>
      <div className="flex items-center space-x-2">
        <Switch
          id="featured"
          checked={formData.featured}
          onCheckedChange={(checked) => onFieldChange('featured', checked)}
          disabled={isLoading}
        />
        <Label htmlFor="featured">Featured Episode</Label>
      </div>
    </>
  );
};
